import type { ReactNode } from "react";
import { navigate, ROUTES } from "../lib/router";

interface Props {
  title: string;
  /** 最終更新日 (例: "2026-04-13")。静的ページ下部のフッタに表示する。 */
  lastUpdated?: string;
  children: ReactNode;
}

/**
 * 静的コンテンツページ (About / Privacy / Terms / Disclaimer) の共通レイアウト。
 * Map SPA と違い、記事スタイル (prose) で縦長スクロール可能にする。
 *
 * SEO 観点: `<main>` + `<h1>` + 説明的な本文を持たせ、Googlebot が
 * ページ内容を正しく抽出できるようにする。
 * AdSense 観点: ナビゲーション (上部バー + ホームへの遷移ボタン) が常に
 * 表示されており、どのページからもトップに戻れる。
 */
export function PageLayout({ title, lastUpdated, children }: Props) {
  return (
    <div className="flex min-h-[100dvh] flex-col bg-slate-950 text-slate-100">
      <header className="safe-top sticky top-0 z-20 flex shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-slate-950/95 px-4 pb-2.5 backdrop-blur-xl">
        <button
          type="button"
          onClick={() => navigate(ROUTES.home)}
          aria-label="マップに戻る"
          className="flex items-center gap-2 rounded-md px-1 text-slate-200 transition hover:text-white"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          <span className="text-[13px] font-semibold">マップに戻る</span>
        </button>
        <span className="truncate text-[13px] font-medium text-slate-400">
          {title}
        </span>
      </header>
      <main className="mx-auto w-full max-w-2xl flex-1 px-5 py-8">
        <article className="prose-article">
          <h1 className="mb-6 text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
            {title}
          </h1>
          {children}
        </article>
      </main>
      <PageFooter lastUpdated={lastUpdated} />
    </div>
  );
}

function PageFooter({ lastUpdated }: { lastUpdated?: string }) {
  return (
    <footer className="safe-bottom mt-8 border-t border-white/10 bg-slate-950/80 px-5 py-6">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 text-[12px] text-slate-400">
        <nav aria-label="サイト内ナビゲーション" className="flex flex-wrap gap-x-4 gap-y-2">
          <FooterLink to={ROUTES.home}>ホーム</FooterLink>
          <FooterLink to={ROUTES.about}>このサイトについて</FooterLink>
          <FooterLink to={ROUTES.privacy}>プライバシーポリシー</FooterLink>
          <FooterLink to={ROUTES.terms}>利用規約</FooterLink>
          <FooterLink to={ROUTES.disclaimer}>免責事項</FooterLink>
        </nav>
        <div className="flex flex-col gap-1 text-slate-500">
          <p>© {new Date().getFullYear()} 食べログ ラーメン百名店マップ</p>
          {lastUpdated && <p>最終更新: {lastUpdated}</p>}
          <p className="text-slate-600">
            本サイトは食べログ株式会社および株式会社カカクコムとは一切関係のない非公式サイトです。
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      className="rounded text-slate-300 underline-offset-4 transition hover:text-white hover:underline"
    >
      {children}
    </button>
  );
}
